import { useEffect } from 'react';
import type { DiscountInfo } from '../../types/cart';

interface DiscountInfoModalProps {
  discountInfo: DiscountInfo;
  discountCode: string;
  onClose: () => void;
}

const formatLimit = (limit: number) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(limit);
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export function DiscountInfoModal({ discountInfo, discountCode, onClose }: DiscountInfoModalProps) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const expiresAt = new Date(discountInfo.expires_at);
  const now = new Date();
  const daysUntilExpiration = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
              <svg className="h-4 w-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-800">{discountInfo.display_name || `Cupón ${discountCode.toUpperCase()}`}</div>
              <div className="text-xs text-gray-500">{discountInfo.discount}% de descuento</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Conditions list */}
        <div className="space-y-0 rounded-lg bg-gray-50 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
            Condiciones
          </div>

          {/* Limit */}
          {discountInfo.limit && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <span>Tope máximo: <strong>{formatLimit(discountInfo.limit)}</strong></span>
            </div>
          )}

          {/* Expiration */}
          <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
            <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>
              Vence: <strong>{formatDate(discountInfo.expires_at)}</strong>
              {daysUntilExpiration > 0 && daysUntilExpiration <= 7 && (
                <span className="ml-1 text-amber-600">({daysUntilExpiration} {daysUntilExpiration === 1 ? 'día' : 'días'})</span>
              )}
              {daysUntilExpiration <= 0 && (
                <span className="ml-1 text-red-600">(vence hoy)</span>
              )}
            </span>
          </div>

          {/* Neighborhood */}
          {discountInfo.neighborhood && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>Solo válido en <strong>{discountInfo.neighborhood}</strong></span>
            </div>
          )}

          {/* Location (nearby offer) */}
          {discountInfo.location_address && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              <span>Solo válido para <strong>{discountInfo.location_address}</strong></span>
            </div>
          )}

          {/* Delivery slot (nearby offer) */}
          {discountInfo.delivery_slot && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Solo válido para el turno del <strong>{discountInfo.delivery_slot}</strong></span>
            </div>
          )}

          {/* Once per user */}
          {discountInfo.once_per_user && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span>Solo podrás usarlo <strong>una vez</strong></span>
            </div>
          )}

          {/* Companies only */}
          {discountInfo.companies_only && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
                <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
              </svg>
              <span>Solo para compras a nombre de una <strong>empresa</strong></span>
            </div>
          )}

          {/* Once per company */}
          {discountInfo.once_per_company && (
            <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v6m0 0l3-3m-3 3l-3-3" />
                <rect x="3" y="10" width="18" height="12" rx="2" />
              </svg>
              <span>Solo para la <strong>primera compra</strong> de la empresa</span>
            </div>
          )}

          {/* Category restrictions */}
          {discountInfo.restricted_categories && discountInfo.restricted_categories.length > 0 && (
            <div className="flex items-start gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 7h16M4 12h16M4 17h10" />
              </svg>
              <span>Solo válido en: <strong>{discountInfo.restricted_categories.join(', ')}</strong></span>
            </div>
          )}

          {/* Excluded products */}
          {discountInfo.excluded_products.length === 0 ? (
            <div className="flex items-center gap-2.5 py-2 text-sm text-gray-600">
              <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span>No aplica a formatos <strong>mayoristas</strong> ni a <strong>huevos</strong></span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2.5 border-b border-gray-200 py-2 text-sm text-gray-600">
                <svg className="h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <span>No incluye formatos <strong>mayoristas</strong></span>
              </div>
              <div className="flex items-start gap-2.5 py-2 text-sm text-gray-600">
                <svg className="h-4 w-4 flex-shrink-0 text-gray-400 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span>Excluye: <strong>{discountInfo.excluded_products.join(', ')}</strong></span>
              </div>
            </>
          )}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-lg bg-gray-100 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
        >
          Entendido
        </button>
      </div>
    </div>
  );
}
